import { useTranslation } from 'react-i18next';
import {
  ItemsListEmptyState,
  Loader,
  ModalComponentWrapper,
  ResultContent,
} from '@/shared/components';
import { useBanksDictionary } from '@/shared/api';
import { cn } from '@/shared/utils';
import UnsuccessDocumentsIcon from '@/shared/assets/unsuccess-documents-icon.svg?react';
import { usePaidInvoices } from '../api';
import { getPaidInvoiceBankName } from '../lib';
import {
  usePaidInvoiceDetailsModal,
  usePaidInvoicesListModal,
  type PaidInvoiceListItem,
  type PaidInvoicesListModalProps,
} from '../model';
import { PaidInvoicesListContent } from './PaidInvoicesListContent';

const I18N_ROOT = 'personalAccount.userProfile.myDocuments.paidInvoices';

const PaidInvoicesListModal = ({
  params,
  onBack,
}: PaidInvoicesListModalProps) => {
  const { t } = useTranslation();
  const { data: invoices, isLoading, isError } = usePaidInvoices(params);
  const { banksData } = useBanksDictionary();
  const { openPaidInvoicesListModal } = usePaidInvoicesListModal();
  const { openPaidInvoiceDetailsModal } = usePaidInvoiceDetailsModal();

  const fallbackBankName = t(`${I18N_ROOT}.defaultBankName`);

  const invoicesList: PaidInvoiceListItem[] = (invoices ?? []).map(invoice => ({
    ...invoice,
    bankName: getPaidInvoiceBankName(
      invoice,
      banksData?.value,
      fallbackBankName
    ),
  }));

  const isEmpty = !isLoading && !isError && invoicesList.length === 0;

  const handleInvoiceClick = (invoice: PaidInvoiceListItem) => {
    openPaidInvoiceDetailsModal({
      invoice,
      lang: params.lang,
      onBack: () => openPaidInvoicesListModal({ params, onBack }),
    });
  };

  return (
    <ModalComponentWrapper title={t(`${I18N_ROOT}.title`)} onBack={onBack}>
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-[3.2rem] min-h-0 h-full',
          isEmpty || isError ? 'h-[38rem]' : ''
        )}>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <ResultContent
            isSuccess={false}
            showButton={false}
            icon={<UnsuccessDocumentsIcon className="size-[6.4rem]" />}
            title={t(`${I18N_ROOT}.error.title`)}
            description={t(`${I18N_ROOT}.error.description`)}
            titleClassName="headline4"
            descriptionClassName="p4"
            className="h-auto"
          />
        ) : isEmpty ? (
          <ItemsListEmptyState
            title={t(`${I18N_ROOT}.empty.title`)}
            description={t(`${I18N_ROOT}.empty.description`)}
          />
        ) : (
          <PaidInvoicesListContent
            invoices={invoicesList}
            onInvoiceClick={handleInvoiceClick}
          />
        )}
      </div>
    </ModalComponentWrapper>
  );
};

export { PaidInvoicesListModal };
